import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle, Calendar, MapPin, Download, Share2, Ticket, Loader, ExternalLink } from "lucide-react";
import { getTicketByPurchaseId } from "../../services/TicketingService";

const TicketSuccess = () => {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const purchaseId = searchParams.get("purchase_id");

  const [ticketData, setTicketData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [shareSuccess, setShareSuccess] = useState(false);

  useEffect(() => {
    if (purchaseId) {
      loadTicket();
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseId]);

  const loadTicket = async () => {
    const result = await getTicketByPurchaseId(purchaseId);
    if (result.success) setTicketData(result);
    setIsLoading(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  };

  const buildGoogleCalUrl = () => {
    const ev = ticketData?.event;
    if (!ev?.startDate) return null;
    const date = ev.startDate.replace(/-/g, "");
    const title = encodeURIComponent(ev.eventName || "Event");
    const location = encodeURIComponent(ev.location || "");
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${date}/${date}&location=${location}`;
  };

  const buildICSUrl = () => {
    const ev = ticketData?.event;
    if (!ev?.startDate) return null;
    const date = ev.startDate.replace(/-/g, "");
    const title = ev.eventName || "Event";
    const location = ev.location || "";
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "BEGIN:VEVENT",
      `DTSTART:${date}`,
      `DTEND:${date}`,
      `SUMMARY:${title}`,
      `LOCATION:${location}`,
      `DESCRIPTION:Your ticket ID: ${purchaseId}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\n");
    return "data:text/calendar;charset=utf-8," + encodeURIComponent(ics);
  };

  const handleShare = async () => {
    const url = window.location.origin + `/events/${eventId}`;
    const title = ticketData?.event?.eventName || "Event";
    if (navigator.share) {
      try {
        await navigator.share({ title, text: `I'm going to ${title}! Get your tickets here:`, url });
      } catch (_) {}
    } else {
      await navigator.clipboard.writeText(url);
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 2500);
    }
  };

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-grey-50">
      <Loader className="w-8 h-8 text-grey-400 animate-spin" />
    </div>
  );

  const googleCalUrl = buildGoogleCalUrl();
  const icsUrl = buildICSUrl();
  const ev = ticketData?.event;
  const purchase = ticketData?.purchase;

  return (
    <div className="min-h-screen bg-grey-50 py-12 px-4">
      <div className="max-w-md mx-auto space-y-4">

        {/* Success header */}
        <div className="bg-white rounded-2xl border border-grey-200 p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-xl font-semibold text-grey-900 mb-1">You're going!</h1>
          <p className="text-grey-500 text-sm">
            {purchase?.buyerEmail
              ? `Confirmation sent to ${purchase.buyerEmail}`
              : "Your ticket has been confirmed."}
          </p>
        </div>

        {/* Ticket card with QR */}
        {purchaseId && (
          <div className="bg-white rounded-2xl border border-grey-200 overflow-hidden">
            {/* Ticket header */}
            <div className="bg-grey-900 px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-white font-semibold text-sm">{ev?.eventName || "Your Ticket"}</p>
                {purchase?.ticketTypeName && (
                  <p className="text-grey-400 text-xs mt-0.5">{purchase.ticketTypeName}</p>
                )}
              </div>
              <Ticket className="w-5 h-5 text-grey-400" />
            </div>

            {/* Dashed divider */}
            <div className="flex items-center px-6">
              <div className="w-4 h-4 rounded-full bg-grey-50 border border-grey-200 -ml-8 flex-shrink-0" />
              <div className="flex-1 border-t-2 border-dashed border-grey-200 mx-2" />
              <div className="w-4 h-4 rounded-full bg-grey-50 border border-grey-200 -mr-8 flex-shrink-0" />
            </div>

            {/* QR code */}
            <div className="px-6 py-6 flex flex-col items-center">
              <div className="bg-white p-4 rounded-xl border border-grey-100 shadow-sm mb-4">
                <QRCodeSVG
                  value={purchaseId}
                  size={160}
                  level="M"
                  includeMargin={false}
                />
              </div>
              <p className="text-xs text-grey-400 text-center">Show this QR code at the door</p>
              <p className="text-xs text-grey-300 font-mono mt-1">{purchaseId.slice(0, 12)}...</p>
            </div>

            {/* Event details */}
            {(ev?.startDate || ev?.location) && (
              <div className="px-6 pb-6 space-y-2">
                {ev?.startDate && (
                  <div className="flex items-center gap-2 text-sm text-grey-600">
                    <Calendar className="w-4 h-4 text-grey-400 flex-shrink-0" />
                    <span>{formatDate(ev.startDate)}{ev.startTime ? ` · ${ev.startTime}` : ""}</span>
                  </div>
                )}
                {ev?.location && (
                  <div className="flex items-center gap-2 text-sm text-grey-600">
                    <MapPin className="w-4 h-4 text-grey-400 flex-shrink-0" />
                    <span>{ev.location}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Calendar + Share actions */}
        <div className="bg-white rounded-2xl border border-grey-200 p-4 space-y-2">
          {googleCalUrl && (
            <a
              href={googleCalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-grey-50 transition"
            >
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Calendar className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-grey-900">Add to Google Calendar</p>
                <p className="text-xs text-grey-400">Save the date</p>
              </div>
              <ExternalLink className="w-4 h-4 text-grey-300" />
            </a>
          )}

          {icsUrl && (
            <a
              href={icsUrl}
              download={`${ev?.eventName || "event"}.ics`}
              className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-grey-50 transition"
            >
              <div className="w-8 h-8 bg-grey-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Download className="w-4 h-4 text-grey-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-grey-900">Download .ics file</p>
                <p className="text-xs text-grey-400">Apple Calendar, Outlook & others</p>
              </div>
            </a>
          )}

          <button
            onClick={handleShare}
            className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-grey-50 transition w-full text-left"
          >
            <div className="w-8 h-8 bg-grey-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Share2 className="w-4 h-4 text-grey-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-grey-900">
                {shareSuccess ? "Link copied!" : "Tell your friends"}
              </p>
              <p className="text-xs text-grey-400">Share the event page</p>
            </div>
            {shareSuccess && <CheckCircle className="w-4 h-4 text-green-500" />}
          </button>
        </div>

        <div className="text-center">
          <Link to={`/events/${eventId}`} className="text-sm text-grey-400 hover:text-grey-600 transition">
            Back to event
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TicketSuccess;
